import { ChatBase } from './chat-base';
import { VideoSessionStatus } from 'common/common/constants/video-session-status';
import { DynamicQueryEvent } from 'common/dynamicquery/dynamic-query-events.factory';
import { ActiveAssistStatus } from 'common/common/constants/active-assist-status';

/**
 * Chat model
 *  <fields>
 <field name="ChatID"  type="long" generateIfNull="true"/>
 <field name="VisitorClientID" type="long"/>
 <field name="ChatType"  type="byte" min="0" max="8"/>
 <field name="ChatWindowDefID" type="long"/>
 <field name="ChatAPISettingsID" type="long"/>
 <field name="ChatButtonDefID" type="long"/>
 <field name="WebsiteDefID" type="long"/>
 <field name="Experiments" type="binary" />
 <field name="ExperimentDate" type="datetime" />
 <field name="DepartmentID"  type="long"/>
 <field name="LastAssignedByOperatorID"   type="long"/>
 <field name="OperatorID"  type="long"/>
 <field name="RedirectStatus" type="byte"/>
 <field name="LanguageCode"  type="text" size="5" default="en-US"/>
 <field name="OperatorLanguageCode"  type="text" size="8" />
 <field name="LastOperatorLanguageCode" type="text" size="8" />
 <field name="Embedded" type="byte" default="0" />
 <field name="VisitID" type="long"/>
 <field name="VisitorID" type="long"/>
 <field name="DueDate" type="datetime"/>
 <field name="VisitCreated" type="datetime"/>
 <field name="ClientType" type="byte" default="0" />
 <field name="RelatedToItemType" type="byte" default="0"/>
 <field name="RelatedToItemID" type="long"/>
 <field name="FolderID"  type="long"/>
 <field name="AccountID" type="long" useAccountIDIfNull="true"/>
 <field name="VisitorMessageCount" type="integer" default="0"/>
 <field name="OperatorMessageCount" type="integer" default="0"/>
 <field name="UnrespondedMessageCount" type="integer" default="0"/>
 <field name="PageType" type="byte" min="0" max="14"/>
 <field name="Secured" type="byte" default="0" />
 <field name="AutoTranslate" type="byte" default="0" />
 <field name="AutoTranslateAllowOriginal" type="byte" default="0" />
 <field name="IP" type="text" size="15"/>
 <field name="ReverseIP" type="text" size="50"/>
 <field name="ACDReassignCount" type="integer" default="0"/>
 <field name="TotalReassignCount" type="integer" default="0"/>
 <field name="MaxQueuePosition" type="integer" default="0"/>
 <field name="ResponseMessageCount" type="integer" default="0"/>
 <field name="TotalResponseTime" type="integer" default="0"/>
 <field name="LastMessagePersonType"      type="byte" min="0" max="1" default="0"/>
 <field name="LastVisitorMessageSent" type="datetime"/>
 <field name="CountryCode" type="text"/>
 <field name="City" type="text"/>
 <field name="RegionCode" type="text"/>
 <field name="AreaCode" type="integer"/>
 <field name="VisitRef" type="text"/>
 <field name="VisitInfo" type="text"/>
 <field name="VisitEmailAddress" type="text"/>
 <field name="VisitPhone" type="text"/>
 <field name="InitialQuestion" type="text"/>
 <field name="WindowClosed" type="datetime"/>
 <field name="ChatStatusType" type="byte" default="0"/>
 <field name="CustomFields" type="stringMap"/>
 <field name="OperatorCustomFields" type="stringMap"/>
 <field name="SurveyResponsiveness" type="integer" min="0" max="5"/>
 <field name="SurveyProfessionalism" type="integer"  min="0" max="5"/>
 <field name="SurveyKnowledge" type="integer"  min="0" max="5"/>
 <field name="SurveyOverall" type="integer"  min="0" max="5"/>
 <field name="SurveyNps" type="integer"  min="0" max="10"/>
 <field name="SurveyComments" type="text"/>
 <field name="UnavailableEmailSubject" type="text"/>
 <field name="UnavailableEmailBody" type="text"/>
 <field name="XmppUserID" type="long" />
 <field name="XmppUser" type="text" />
 <field name="XmppThread" type="text" />
 <field name="XmppClientID" type="long" />
 <field name="Flagged" type="datetime"/>
 <field name="Created" type="created" allowsUpdates="false"/>
 <field name="Answered" type="datetime"/>
 <field name="LastAssignedAnswered" type="datetime"/>
 <field name="Started" type="datetime"/>
 <field name="Ended" type="datetime"/>
 <field name="Closed" type="datetime"/>
 <field name="UserCategoryID" type="long"/>
 <field name="UserStatusID" type="long"/>
 <field name="CustomField1ID" type="long"/>
 <field name="CustomField2ID" type="long"/>
 <field name="ChatName" type="text"/>
 <field name="ChatUrl" type="text"/>
 <field name="CustomUrl" type="text"/>
 <field name="Updated" type="updated"/>
 <field name="Deleted" type="datetime"/>
 <field name="DeletedBy" type="long"/>
 <field name="LastName" type="text"/>
 <field name="EndedBy" type="long"/>
 <field name="EndedReasonType" type="byte"/>
 <field name="BlockedBy" type="long"/>
 <field name="FlaggedBy" type="long"/>
 <field name="ExternalCrmItemID" type="text"/>
 <field name="ExternalCrmListID" type="text"/>
 <field name="ExternalCrmType" type="byte"/>
 <field name="IncidentID" type="long"/>
 <field name="ActiveAssistDisabled" type="integer" default="0"/>
 <field name="ActiveAssistStatus" type="byte" default="0"/>
 <field name="InvitationTemplateVariantID" type="long" />
 <field name="ScheduleHistoryToEmailAddress" type="text"/>
 <field name="Priority" type="byte"/> <!-- Default has to be null -->
 <field name="AssignedByAPI" type="byte" />
 <field name="InitialDepartmentID" type="long" />
 <field name="LastACDStarted" type="datetime"/>
 </fields>
 *
 * @param chat
 * @constructor
 */
class Chat extends ChatBase {
  public ChatID;
  public ChatWindowDefID;
  public LanguageCode;
  public WebsiteDefID;
  public ClientType;
  public VideoSessionStatus: VideoSessionStatus;
  public ActiveAssistStatus: ActiveAssistStatus;
  public ChatName: string;

  static comparator(chat1, chat2) {
    return chat1.ChatID === chat2.ChatID;
  }

  static wrap(event: Partial<DynamicQueryEvent>): Chat {
    return <Chat>super.wrap(event);
  }
}

function factory() {
  return Chat;
}

export { Chat, factory };
